import { ComponentProps } from "react";
import * as RechartsPrimitive from "recharts";
import { Circle, styled, Text, useMedia, View, XStack } from "tamagui";

import {
  ChartConfig,
  ChartContainer,
  useChartContext,
} from "./chart-container";

type TickProps = {
  textAnchor: "start" | "middle" | "end";
  verticalAnchor: "start" | "middle" | "end";
  orientation: "top" | "bottom";
  width: number;
  height: number;
  x: number;
  y: number;
  className: string;
  stroke: string;
  fill: string;
  index: number;
  visibleTicksCount?: number;
  payload: {
    coordinate: number;
    value: string | number;
    index: number;
    offset: number;
    tickCoord: number;
    isShow?: boolean;
  };
};

const XAxisTick: React.FC<
  TickProps & {
    tickFormatter?: (value: string | number) => string | number;
  }
> = ({ x, y, payload, textAnchor, tickFormatter }) => {
  return (
    <text
      textAnchor={textAnchor}
      x={x}
      y={y}
      fill="#636668"
      fontFamily="Fixel Text"
      fontSize={14}
      fontWeight={400}
    >
      {tickFormatter ? tickFormatter(payload.value) : payload.value}
    </text>
  );
};

const YAxisTick: React.FC<
  TickProps & {
    tickFormatter?: (value: string | number) => string | number;
  }
> = ({ x, y, payload, textAnchor, tickFormatter }) => {
  return (
    <text
      textAnchor={textAnchor}
      x={x}
      y={y}
      fill="#636668"
      fontFamily="Fixel Text"
      fontSize={14}
      fontWeight={500}
    >
      {tickFormatter ? tickFormatter(payload.value) : payload.value}
    </text>
  );
};

type Payload = NonNullable<
  React.ComponentProps<typeof RechartsPrimitive.Tooltip>["payload"]
>[number];

const TooltipContent: React.FC<
  React.ComponentProps<typeof RechartsPrimitive.Tooltip> & {
    children: (
      payload: (Payload & { config?: ChartConfig[number] })[],
      xAxisLabel: string,
      date: string | undefined,
    ) => React.ReactNode;
  }
> = (props) => {
  const { config } = useChartContext();
  const { payload, children, label } = props ?? {};
  if (!payload) return null;

  const payloadData = payload.map((p) => {
    return {
      config: p?.dataKey ? config[p?.dataKey] : {},
      ...p,
    };
  });

  //TODO: validate payload data
  const date = payload[0]?.payload?.date as string | undefined;

  return children(payloadData, label, date);
};

const TooltipContainer = styled(View, {
  name: "TooltipContainer",
  backgroundColor: "white",
  gap: "$1.3",
  borderColor: "$gray-220",
  borderWidth: "$0.2",
  variants: {
    size: {
      lg: {
        paddingHorizontal: "$2.5",
        paddingVertical: "$2",
      },
    },
    rounded: {
      lg: {
        borderRadius: "$2",
      },
    },
  } as const,
  defaultVariants: {
    size: "lg",
    rounded: "lg",
  },
});

const TooltipTitle = styled(Text, {
  name: "TooltipTitle",
  fontFamily: "$body",
  color: "black",

  variants: {
    font: {
      lg: {
        fontSize: "$3",
        fontWeight: "$5",
      },
    },
  } as const,
  defaultVariants: {
    font: "lg",
  },
});

const ChartTooltip = {
  Content: TooltipContent,
  Container: TooltipContainer,
  Title: TooltipTitle,
};

const LegendContainer = styled(View, {
  name: "LegendContainer",
  backgroundColor: "$warm-snow-500",
  flexDirection: "row",
  flexWrap: "wrap",
  variants: {
    size: {
      lg: {
        paddingVertical: "$2",
        paddingHorizontal: "$3",
        gap: "$3",
      },
      md: {
        paddingVertical: "$2",
        paddingHorizontal: "$3",
        gap: "$1.5",
      },
    },
    rounded: {
      lg: {
        borderRadius: "$5",
        borderWidth: "$0.1",
        borderStyle: "solid",
        borderColor: "$gray-200",
      },
    },
  } as const,
  defaultVariants: {
    size: "lg",
    rounded: "lg",
  },
});

const LegendCircle = styled(Circle, {
  width: "$1.3",
  height: "$1.3",
});

const LegendText = styled(Text, {
  name: "LegendText",
  fontFamily: "$subtitle",
  fontWeight: "$5",
  fontSize: "$2",
  lineHeight: "$2",
});

type LegendProps = ComponentProps<typeof LegendContainer>;

export const Legend: React.FC<LegendProps & { config: ChartConfig }> = ({
  config,
  ...rest
}) => {
  const { sm } = useMedia();
  return (
    <LegendContainer
      size={sm ? "md" : "lg"}
      data-id={"legend-container"}
      {...rest}
    >
      {getChartLines(config).map(({ color, label }, i) => (
        <XStack
          data-id={"legend-item"}
          data-label={label}
          gap={"$1"}
          alignItems="center"
          key={i}
        >
          <LegendCircle backgroundColor={color} />
          <LegendText>{label}</LegendText>
        </XStack>
      ))}
    </LegendContainer>
  );
};

const getItemIndex = <
  T extends Record<string, unknown>[],
  K extends keyof T[number],
>(
  data: T,
  xAxisLabelKey: string,
  xAxisLabel: T[number][K],
) => data.findIndex((v) => v[xAxisLabelKey] === xAxisLabel);

const getChartLines = (config: ChartConfig) =>
  Object.entries(config).map((conf) => {
    return {
      dataKey: conf[0],
      ...conf[1],
    };
  });

export {
  ChartContainer,
  ChartTooltip,
  getChartLines,
  getItemIndex,
  useChartContext,
  XAxisTick,
  YAxisTick,
  type ChartConfig,
  type TickProps,
};
